import { defaultServiceLanguage, ServiceLanguageMap } from "@albertine/i18n";
import {
    type HasUsedConciergeAnswer,
    HasUsedConciergeAnswerTextMapSchema,
    type HasUsedConciergeFor,
    HasUsedConciergeForSchema,
    HasUsedConciergeForTextMapSchema,
    type MemberDataFirestoreRecord,
    MemberSourceIdMap,
    type UpcomingDiningPlansAnswer,
    type UpcomingTravelPlansAnswer,
} from "@albertine/types";
import { isEmpty, isString } from "../../common/utils/string.util";
import { isTypeOf } from "../../common/utils/zod.util";

export const upcomingDiningPlanInfo = (
    intent: UpcomingDiningPlansAnswer,
    query: string | undefined,
): string => {
    if (intent === "yes") {
        return query
            ? `Has dining plans coming up: ${query}`
            : "Has dining plans coming up. No details were added, so please ask the member.";
    }
    if (intent === "no") {
        return "Nothing in the near future";
    }

    return "-";
};

export const upcomingTravelPlanInfo = (
    intent: UpcomingTravelPlansAnswer,
    query: string | undefined,
): string => {
    if (intent === "yes") {
        return query
            ? `A specific trip coming up: ${query}`
            : "A specific trip coming up. No details were added, so please ask the member.";
    }
    if (intent === "some") {
        return query
            ? `Some plans, but still on an idea level: ${query}`
            : "Some plans, but still on an idea level. No details were added, so please ask the member.";
    }
    if (intent === "no") {
        return "Nothing in the near future";
    }

    return "-";
};

export function formatSource(member: MemberDataFirestoreRecord): string | undefined {
    const source = member.source ? MemberSourceIdMap[member.source] : undefined;
    const additionalNote = member.additionalNoteOnSource
        ? `, ${member.additionalNoteOnSource}`
        : "";

    return source ? `${source}${additionalNote}` : undefined;
}

export function formatServiceLanguage(member: MemberDataFirestoreRecord): string {
    return member.serviceLanguage && ServiceLanguageMap[member.serviceLanguage]
        ? ServiceLanguageMap[member.serviceLanguage]
        : ServiceLanguageMap[defaultServiceLanguage];
}

export function formatAddress(member: MemberDataFirestoreRecord): string | undefined {
    const { address, postalCode, city, country, state } = member;

    const result: string[] = [];

    if (isString(address) && !isEmpty(address)) {
        result.push(address);
    }

    const addressPostalCode =
        isString(postalCode) && !isEmpty(postalCode) ? postalCode : undefined;
    const addressCity = isString(city) && !isEmpty(city) ? city : undefined;

    if (addressPostalCode && addressCity) {
        result.push(`${addressPostalCode} ${addressCity}`);
    } else {
        if (addressPostalCode) {
            result.push(addressPostalCode);
        }

        if (addressCity) {
            result.push(addressCity);
        }
    }

    if (isString(state) && !isEmpty(state)) {
        result.push(state);
    }

    if (isString(country) && !isEmpty(country)) {
        result.push(country);
    }

    return result.length !== 0 ? result.join(",\n") : undefined;
}

export function formatInterests(member: MemberDataFirestoreRecord): string | undefined {
    const { interests, loves, hates } = member;

    const result: string[] = [];

    if (isString(interests) && !isEmpty(interests)) {
        result.push(interests);
    }

    if (isString(loves) && !isEmpty(loves)) {
        result.push(`Really loves: ${loves}`);
    }

    if (isString(hates) && !isEmpty(hates)) {
        result.push(`Really hates: ${hates}`);
    }

    return result.length !== 0 ? result.join("\n") : undefined;
}

export function formatPushNotifications(
    platform: "ios" | "android/web",
    member: MemberDataFirestoreRecord,
): string | undefined {
    if (platform === "ios") {
        if (member.pushNotificationsEnabled) {
            return "Enabled";
        }

        if (member.pushNotificationsEnabled === false) {
            return "Disabled";
        }
    }

    if (platform === "android/web") {
        if (member.pushNotificationsEnabledWeb) {
            return "Enabled";
        }

        if (member.pushNotificationsEnabledWeb === false) {
            return "Disabled";
        }
    }

    return undefined;
}

export function formatOnboardingMustKnow(
    hasUsedConcierge: HasUsedConciergeAnswer | null | undefined,
    hasUsedConciergeFor: HasUsedConciergeFor | string | null | undefined,
) {
    const hasUsedConciergeText = hasUsedConcierge
        ? HasUsedConciergeAnswerTextMapSchema[hasUsedConcierge]
        : undefined;

    const hasUsedConciergeForText = isTypeOf(
        hasUsedConciergeFor,
        HasUsedConciergeForSchema,
    )
        ? HasUsedConciergeForTextMapSchema[hasUsedConciergeFor]
        : (hasUsedConciergeFor ?? undefined);

    if (hasUsedConciergeText && hasUsedConciergeForText) {
        return `${hasUsedConciergeText} Primary reason: ${hasUsedConciergeForText}`;
    }

    return hasUsedConciergeText;
}
